// Mod・プラグイン（Modrinth / CurseForge）の型と、依存解決・mrpack マニフェストの生成。SPEC 11 章。
// 取得元ごとの違いは AddonSource の実装（mods-modrinth.ts / mods-curseforge.ts）に閉じ込める。
import { z } from 'zod';
import { addonKindsOf, addonLoaderOf } from './addons.js';

/** 導入先。Mod ローダー（fabric / neoforge / forge）か、プラグインを動かすソフト（paper / purpur） */
export const ModLoaderSchema = z.enum([
  'fabric',
  'neoforge',
  'forge',
  'velocity',
  'paper',
  'purpur',
  'youer',
  'asyncyouer',
  'mohist',
]);
export type ModLoader = z.infer<typeof ModLoaderSchema>;
export const PackLoaderSchema = z.enum(['fabric', 'neoforge', 'forge']);
export type PackLoader = z.infer<typeof PackLoaderSchema>;
export const isPluginPlatform = (loader: ModLoader) => addonKindsOf(loader).plugins && !addonKindsOf(loader).mods;

export const AddonSourceNameSchema = z.enum(['modrinth', 'curseforge']);
export type AddonSourceName = z.infer<typeof AddonSourceNameSchema>;

export const SideSupportSchema = z.enum(['required', 'optional', 'unsupported', 'unknown']);
export type SideSupport = z.infer<typeof SideSupportSchema>;

export const ManualModSchema = z.object({
  pageUrl: z.string().regex(/^https:\/\/www\.curseforge\.com\/minecraft\/mc-mods\/[a-z0-9-]+\/files\/[1-9][0-9]*$/),
});

export const ModFileSchema = z.object({
  url: z.string().url(),
  filename: z.string().regex(/^[^/\\]+\.jar$/),
  sha1: z.string().regex(/^[0-9a-f]{40}$/),
  /** Modrinth は sha512 も出す。CurseForge は sha1 だけ */
  sha512: z
    .string()
    .regex(/^[0-9a-f]{128}$/)
    .optional(),
  size: z.number().int().nonnegative(),
});
export type ModFile = z.infer<typeof ModFileSchema>;

/** UI に返す 1 つの Mod / プラグイン */
export const ModEntrySchema = z.object({
  manual: ManualModSchema.optional(),
  bundled: z.boolean().optional(),
  source: AddonSourceNameSchema.default('modrinth'),
  kind: z.enum(['mod', 'plugin']).default('mod'),
  projectId: z.string(),
  slug: z.string(),
  title: z.string(),
  versionId: z.string(),
  versionNumber: z.string(),
  iconUrl: z.string().optional(),
  env: z.object({ client: SideSupportSchema, server: SideSupportSchema }),
  clientOverride: SideSupportSchema.exclude(['unknown']).optional(),
  dependency: z.boolean(),
  modpack: z.object({ name: z.string(), versionId: z.string(), path: z.string() }).optional(),
});
export type ModEntry = z.infer<typeof ModEntrySchema>;

/** サーバーに保存するマニフェストの 1 行（UI 用の情報＋ファイル） */
export const InstalledModSchema = ModEntrySchema.extend({ file: ModFileSchema });
export type InstalledMod = z.infer<typeof InstalledModSchema>;

export const ModSpecifierSchema = z
  .object({
    kind: z.enum(['mod', 'plugin']).optional(),
    project: z.string().min(1).max(100),
    version: z.string().min(1).max(100).optional(),
    source: AddonSourceNameSchema.default('modrinth'),
  })
  .strict();
export type ModSpecifier = z.input<typeof ModSpecifierSchema>;

export const ModChangeSchema = z
  .object({
    kind: z.enum(['mod', 'plugin']).optional(),
    add: z.array(ModSpecifierSchema).max(100).default([]),
    remove: z.array(z.string().min(1).max(100)).max(100).default([]),
    update: z.array(z.string().min(1).max(100)).max(100).default([]),
  })
  .strict();
export type ModChange = z.infer<typeof ModChangeSchema>;

export const ModResolveInputSchema = ModChangeSchema.extend({
  loader: ModLoaderSchema,
  mcVersion: z.string().min(1).max(100),
  serverId: z.string().optional(),
}).strict();
export type ModResolveInput = z.infer<typeof ModResolveInputSchema>;

export const ModSearchInputSchema = z
  .object({
    q: z.string().max(100).default(''),
    loader: ModLoaderSchema,
    kind: z.enum(['mod', 'plugin']).optional(),
    mcVersion: z.string().min(1).max(100),
    offset: z.coerce.number().int().min(0).max(1000).default(0),
    source: AddonSourceNameSchema.default('modrinth'),
  })
  .strict();
export interface ModResolveResult {
  entries: ModEntry[];
  added: ModEntry[];
  removed: ModEntry[];
  updated: Array<{ before: ModEntry; after: ModEntry }>;
  errors: Array<{ project: string; message: string }>;
  /** サーバーに保存する完全なマニフェスト（errors が空のときだけ使う） */
  installed: InstalledMod[];
}

export const ModSearchHitSchema = z.object({
  source: AddonSourceNameSchema.default('modrinth'),
  projectId: z.string(),
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  iconUrl: z.string().optional(),
  downloads: z.number(),
  categories: z.array(z.string()),
  versions: z.array(z.string()).optional(),
  clientSide: SideSupportSchema,
  serverSide: SideSupportSchema,
});
export type ModSearchHit = z.infer<typeof ModSearchHitSchema>;

// ---- 取得元の共通の形 ----

export interface AddonProject {
  kind?: 'mod' | 'plugin' | 'unsupported';
  /** 対応ローダーから分かる、入れられる種類（LuckPerms のように両方のこともある）。分かるときは kind より優先する */
  kinds?: ('mod' | 'plugin')[];
  id: string;
  slug: string;
  title: string;
  description: string;
  iconUrl?: string;
  client: SideSupport;
  server: SideSupport;
  /** CurseForge のプロジェクトの種類（6 = Mod、12 = リソースパック、6552 = シェーダー など） */
  classId?: number;
}

export interface AddonVersion {
  id: string;
  projectId: string;
  number: string;
  type: 'release' | 'beta' | 'alpha';
  date: string;
  deps: Array<{ projectId?: string; versionId?: string; type: 'required' | 'optional' | 'incompatible' | 'embedded' }>;
  file?: ModFile;
  /** file が無い理由（配布元が自動ダウンロードを禁止しているなど） */
  fileProblem?: string;
}

export interface AddonSource {
  versionByHash?(sha1: string): Promise<AddonVersion>;
  readonly name: AddonSourceName;
  search(query: string, loader: ModLoader, mcVersion: string, offset?: number): Promise<ModSearchHit[]>;
  project(ref: string): Promise<AddonProject>;
  version(id: string, projectId?: string): Promise<AddonVersion>;
  latestCompatible(projectId: string, loader: ModLoader, mcVersion: string): Promise<AddonVersion | undefined>;
  compatible(version: AddonVersion, loader: ModLoader, mcVersion: string): boolean;
}

export type AddonSources = Partial<Record<AddonSourceName, AddonSource>>;

const sourceLabel: Record<AddonSourceName, string> = { modrinth: 'Modrinth', curseforge: 'CurseForge' };

export function publicEntry(mod: InstalledMod): ModEntry {
  const { file: _file, ...entry } = mod;
  return entry;
}

interface Pick {
  source: AddonSource;
  project: AddonProject;
  version: AddonVersion;
  explicit: boolean;
}

const keyOf = (source: AddonSourceName, projectId: string) => `${source}:${projectId}`;

/**
 * 変更（追加・削除・更新）を現在の構成に当て、required 依存を再帰的に足した最終構成を作る。
 * 依存は「明示的に入れた Mod」から辿れるものだけ残す（不要になった依存は消える）。依存は同じ取得元の中で辿る。
 */
export async function resolveMods(
  sources: AddonSources | AddonSource,
  input: { loader: ModLoader; mcVersion: string; current: InstalledMod[] } & ModChange,
): Promise<ModResolveResult> {
  const kind = input.kind ?? (isPluginPlatform(input.loader) ? 'plugin' : 'mod');
  const loader = addonLoaderOf(input.loader, kind);
  if (!loader)
    throw new Error(
      kind === 'mod' ? 'このソフトには Mod を導入できません' : 'このソフトにはプラグインを導入できません',
    );
  input = { ...input, loader };
  const map: AddonSources = 'name' in sources ? { [(sources as AddonSource).name]: sources } : sources;
  const errors: ModResolveResult['errors'] = [];
  const picks = new Map<string, Pick>();
  const fail = (project: string, message: string) => errors.push({ project, message });
  const target = `${input.loader} ${input.mcVersion}`;
  const word = kind === 'plugin' ? 'プラグイン' : ' Mod ';
  const checkKind = (project: AddonProject) => {
    if (project.kinds?.length ? project.kinds.includes(kind) : !project.kind || project.kind === kind) return true;
    fail(
      project.title,
      project.kind === 'unsupported'
        ? 'Mod やプラグイン以外は導入できません'
        : kind === 'plugin'
          ? 'このソフトには Mod を導入できません'
          : 'このソフトにはプラグインを導入できません',
    );
    return false;
  };
  const sourceFor = (name: AddonSourceName) => {
    const source = map[name];
    if (!source) throw new Error(`${sourceLabel[name]} は設定されていません`);
    return source;
  };

  // 1. 現在の明示的な Mod（削除対象を除く）
  const matches = (m: InstalledMod, ref: string) => m.projectId === ref || m.slug === ref;
  const removeKeys = new Set<string>();
  for (const ref of input.remove) {
    const found = input.current.find((m) => matches(m, ref));
    if (found) removeKeys.add(keyOf(found.source, found.projectId));
    else fail(ref, `導入されていない${word}は削除できません`);
  }
  const updateKeys = new Set(
    input.update.flatMap((ref) => {
      const found = input.current.find((m) => matches(m, ref));
      return found ? [keyOf(found.source, found.projectId)] : [];
    }),
  );
  // ソフトの系統を変えた後に残っている別の種類（Fabric にしたあとのプラグインなど）は、解決せずそのまま残す。
  // agent は種類で振り分けるので使われず、系統を戻せばまた使われる。
  const imported = input.current.filter(
    (mod) =>
      mod.modpack &&
      !removeKeys.has(keyOf(mod.source, mod.projectId)) &&
      !updateKeys.has(keyOf(mod.source, mod.projectId)),
  );
  const dormant = input.current.filter((m) => m.kind !== kind && !removeKeys.has(keyOf(m.source, m.projectId)));
  for (const mod of input.current) {
    const key = keyOf(mod.source, mod.projectId);
    if (mod.dependency || removeKeys.has(key) || mod.kind !== kind || imported.includes(mod)) continue;
    try {
      const source = sourceFor(mod.source);
      const project = await source.project(mod.projectId);
      if (!checkKind(project)) continue;
      const version = updateKeys.has(key)
        ? await source.latestCompatible(project.id, input.loader, input.mcVersion)
        : await source.version(mod.versionId, mod.projectId);
      if (!version) fail(project.title, `${target} に対応する版がありません`);
      else picks.set(key, { source, project, version, explicit: true });
    } catch {
      fail(mod.title, `${sourceLabel[mod.source]} から情報を取得できませんでした`);
    }
  }

  // 2. 追加
  for (const raw of input.add) {
    const spec = ModSpecifierSchema.parse(raw);
    if (spec.kind && spec.kind !== kind) {
      fail(spec.project, '選択したアドオンの種類と追加する種類が一致しません');
      continue;
    }
    try {
      const source = sourceFor(spec.source);
      const project = await source.project(spec.project);
      if (!checkKind(project)) continue;
      // クライアント専用の Mod は、サーバーには置かずランチャーの配布物にだけ入れる（serverMods で外れる）。
      // プラグインはプレイヤーに配らないので、これまでどおり断る
      if (project.server === 'unsupported' && kind === 'plugin') {
        fail(project.title, `クライアント専用の${word}はサーバーに入れられません`);
        continue;
      }
      const version = spec.version
        ? await source.version(spec.version, project.id)
        : await source.latestCompatible(project.id, input.loader, input.mcVersion);
      if (!version && !spec.version) fail(project.title, `${target} に対応する版がありません`);
      else if (!version || version.projectId !== project.id) fail(project.title, '指定の版が見つかりません');
      else if (!source.compatible(version, input.loader, input.mcVersion))
        fail(project.title, `${target} に対応していません（${version.number}）`);
      else picks.set(keyOf(source.name, project.id), { source, project, version, explicit: true });
    } catch (error) {
      fail(
        spec.project,
        error instanceof Error && error.message.includes('設定されていません')
          ? error.message
          : `${sourceLabel[spec.source]} で見つかりません`,
      );
    }
  }

  // 3. required 依存を幅優先で足す（同じ取得元の中で）
  const queue = [...picks.values()];
  while (queue.length) {
    const pick = queue.shift()!;
    for (const dep of pick.version.deps) {
      if (dep.type !== 'required') continue;
      try {
        const depVersion = dep.versionId ? await pick.source.version(dep.versionId, dep.projectId) : undefined;
        const depProjectId = depVersion?.projectId ?? dep.projectId;
        if (!depProjectId || picks.has(keyOf(pick.source.name, depProjectId))) continue;
        const project = await pick.source.project(depProjectId);
        if (!checkKind(project)) continue;
        const version =
          depVersion && pick.source.compatible(depVersion, input.loader, input.mcVersion)
            ? depVersion
            : await pick.source.latestCompatible(project.id, input.loader, input.mcVersion);
        if (!version) {
          fail(project.title, `${pick.project.title} が必要としていますが、${target} 版がありません`);
          continue;
        }
        const next = { source: pick.source, project, version, explicit: false };
        picks.set(keyOf(pick.source.name, project.id), next);
        queue.push(next);
      } catch {
        fail(dep.projectId ?? dep.versionId ?? '?', `${pick.project.title} の依存を取得できませんでした`);
      }
    }
  }

  // 4. 衝突（incompatible）
  for (const pick of picks.values()) {
    for (const dep of pick.version.deps) {
      if (dep.type !== 'incompatible') continue;
      const other = dep.projectId ? picks.get(keyOf(pick.source.name, dep.projectId)) : undefined;
      const byVersion = dep.versionId ? [...picks.values()].find((p) => p.version.id === dep.versionId) : undefined;
      const conflict = other ?? byVersion;
      if (conflict) fail(pick.project.title, `${conflict.project.title} と同時に入れられません`);
    }
  }

  // 5. 結果
  const installed: InstalledMod[] = [];
  for (const pick of picks.values()) {
    if (!pick.version.file) {
      fail(pick.project.title, pick.version.fileProblem ?? 'ダウンロードできるファイルがありません');
      continue;
    }
    installed.push({
      source: pick.source.name,
      kind,
      projectId: pick.project.id,
      slug: pick.project.slug,
      title: pick.project.title,
      versionId: pick.version.id,
      versionNumber: pick.version.number,
      iconUrl: pick.project.iconUrl,
      env: { client: pick.project.client, server: pick.project.server },
      dependency: !pick.explicit,
      file: pick.version.file,
      clientOverride: input.current.find((m) => m.source === pick.source.name && m.projectId === pick.project.id)
        ?.clientOverride,
    });
  }
  installed.sort((a, b) => Number(a.dependency) - Number(b.dependency) || a.title.localeCompare(b.title));
  installed.push(...dormant, ...imported.filter((mod) => mod.kind === kind));

  const before = new Map(input.current.map((m) => [keyOf(m.source, m.projectId), m]));
  const after = new Map(installed.map((m) => [keyOf(m.source, m.projectId), m]));
  return {
    entries: installed.filter((m) => m.kind === kind).map(publicEntry),
    installed,
    added: installed.filter((m) => !before.has(keyOf(m.source, m.projectId))).map(publicEntry),
    removed: input.current.filter((m) => !after.has(keyOf(m.source, m.projectId))).map(publicEntry),
    updated: installed
      .filter((m) => {
        const old = before.get(keyOf(m.source, m.projectId));
        return old && old.versionId !== m.versionId;
      })
      .map((m) => ({ before: publicEntry(before.get(keyOf(m.source, m.projectId))!), after: publicEntry(m) })),
    errors,
  };
}

// ---- mrpack（modrinth.index.json） ----

export const MrpackIndexSchema = z.object({
  formatVersion: z.literal(1),
  game: z.literal('minecraft'),
  versionId: z.string(),
  name: z.string(),
  summary: z.string().optional(),
  files: z.array(
    z.object({
      path: z.string(),
      hashes: z.object({ sha1: z.string(), sha512: z.string() }),
      env: z.object({
        client: z.enum(['required', 'optional', 'unsupported']),
        server: z.enum(['required', 'optional', 'unsupported']),
      }),
      downloads: z.array(z.string().url()),
      fileSize: z.number().int(),
    }),
  ),
  dependencies: z.record(z.string(), z.string()),
});
export type MrpackIndex = z.infer<typeof MrpackIndexSchema>;

const loaderKey: Record<PackLoader, string> = { fabric: 'fabric-loader', neoforge: 'neoforge', forge: 'forge' };

function packSide(value: SideSupport): 'required' | 'optional' | 'unsupported' {
  return value === 'unknown' ? 'required' : value;
}

/** mrpack に入れられる Mod（Modrinth のもので sha512 があるもの）。CurseForge のものは packwiz で配る */
export function mrpackEligible(mod: InstalledMod): boolean {
  return (
    mod.kind === 'mod' &&
    mod.source === 'modrinth' &&
    mod.file.sha512 !== undefined &&
    !mod.file.url.startsWith('data:')
  );
}

/** サーバー用（side=server）かクライアント用（side=client）の mrpack インデックスを作る */
export function buildMrpackIndex(input: {
  name: string;
  versionId: string;
  mcVersion: string;
  loader: PackLoader;
  loaderVersion?: string;
  mods: InstalledMod[];
  side: 'server' | 'client';
}): MrpackIndex {
  const files = input.mods
    .filter((mod) => mrpackEligible(mod) && mod.env[input.side] !== 'unsupported')
    .map((mod) => ({
      path: `mods/${mod.file.filename}`,
      hashes: { sha1: mod.file.sha1, sha512: mod.file.sha512! },
      env: { client: packSide(mod.env.client), server: packSide(mod.env.server) },
      downloads: [mod.file.url],
      fileSize: mod.file.size,
    }));
  const dependencies: Record<string, string> = { minecraft: input.mcVersion };
  if (input.loaderVersion) dependencies[loaderKey[input.loader]] = input.loaderVersion;
  return { formatVersion: 1, game: 'minecraft', versionId: input.versionId, name: input.name, files, dependencies };
}

/** サーバーに入れるファイル（env.server が unsupported 以外） */
export function serverMods(mods: InstalledMod[]): InstalledMod[] {
  return mods.filter((mod) => mod.env.server !== 'unsupported');
}

/** クライアント専用（サーバーには置かず、ランチャーの配布物にだけ入る）Mod か */
export function clientOnlyMod(mod: { kind: InstalledMod['kind']; env: InstalledMod['env'] }): boolean {
  return mod.kind === 'mod' && mod.env.server === 'unsupported';
}

/**
 * サーバーに置くファイルが変わるか。クライアント専用の Mod だけの追加・削除・更新なら false で、
 * サーバーを止めずに配布物だけを変えられる
 */
export function serverFilesChanged(before: InstalledMod[], after: InstalledMod[]): boolean {
  const files = (mods: InstalledMod[]) =>
    serverMods(mods)
      .map((mod) => `${mod.kind}:${mod.file.filename}:${mod.file.sha1}`)
      .sort()
      .join('\n');
  return files(before) !== files(after);
}

export function launcherSide(mod: {
  env: ModEntry['env'];
  clientOverride?: ModEntry['clientOverride'];
  source?: ModEntry['source'];
  modpack?: unknown;
  bundled?: boolean;
}): 'required' | 'optional' | 'server' {
  const side = mod.clientOverride ?? mod.env.client;
  // CurseForge の Modpack の Mod は、CurseForge のタグ（Server だけ）で配らないことにしない。
  // タグは当てにならず（Oh The Trees You'll Grow は Server だけのタグだがクライアントにも要る）、公式アプリも Prism も全部入れる。
  // 同梱の jar（server-overrides/mods など）は作者が置き場所で決めているので、そのまま
  if (side === 'unsupported' && !mod.clientOverride && !mod.bundled && mod.source === 'curseforge' && mod.modpack)
    return 'required';
  return side === 'unsupported' ? 'server' : side === 'unknown' ? 'required' : side;
}
